/**
 * cvt-3d-model.ts - Módulo de Modelagem Paramétrica 3D & Cinemática WebGL
 * Domínio: Manutenção Técnica Concessionária Honda ADV 150 (Projeto K0W / Bloco KF43E)
 * Filosofia Fábio Akita: Boring Technology, Deep Modules, Zero Mocks e Fail-Visible.
 */

export interface Part3DConfig {
  id: string;
  name: string;
  partNumber: string;
  oemSupplier: string;
  torqueSpec: string;
  serviceLimitMm?: number;
  nominalDimensionMm?: number;
  criticalGotcha: string;
  requiredTool: string;
  baseZ: number; // Posição montada no eixo Z (mm / unidades de cena)
  explodedZ: number; // Posição totalmente explodida no eixo Z
  colorHex: string;
  metalness: number;
  roughness: number;
  isTransparent?: boolean;
}

export const CVT_PARTS_3D_METROLOGY: ReadonlyArray<Part3DConfig> = [
  {
    id: "part-01-crankshaft",
    name: "Ponta do Virabrequim (Eixo Motriz KF43E)",
    partNumber: "13000-K0W-B00",
    oemSupplier: "Honda Dream OEM",
    torqueSpec: "Eixo de Tração Rotativa",
    criticalGotcha:
      "Estrias M14 e rosca de precisão. Proibido bater com martelo ou apoiar alavancas na carcaça do cárter.",
    requiredTool: "Soquete 17 mm (giro do virabrequim)",
    baseZ: -80,
    explodedZ: -120,
    colorHex: "#94a3b8",
    metalness: 0.95,
    roughness: 0.18,
  },
  {
    id: "part-02-ramp-plate",
    name: "Placa de Rampa + 3 Cursores Guia (Slide Pieces)",
    partNumber: "22011-K97-T00",
    oemSupplier: "FCC / Honda",
    torqueSpec: "Encaixe estriado deslizante",
    criticalGotcha:
      "Os 3 cursores plásticos não podem apresentar folga lateral nos trilhos para evitar vibração e ruído metálico.",
    requiredTool: "Inspeção visual / Manual",
    baseZ: -55,
    explodedZ: -75,
    colorHex: "#334155",
    metalness: 0.85,
    roughness: 0.35,
  },
  {
    id: "part-03-rollers",
    name: "Jogo de 6 Roletes Centrífugos (21g cada)",
    partNumber: "22123-K0W-B00",
    oemSupplier: "Bando / Honda",
    torqueSpec: "Alojados a seco nas 6 pistas curvas",
    serviceLimitMm: 19.5,
    nominalDimensionMm: 20.0,
    criticalGotcha:
      "MONTAGEM 100% A SECO! NUNCA aplicar graxa nos roletes. Limpeza exclusiva com álcool isopropílico 99%.",
    requiredTool: "Paquímetro digital centesimal (limite: 19,5 mm)",
    baseZ: -45,
    explodedZ: -40,
    colorHex: "#0284c7",
    metalness: 0.3,
    roughness: 0.4,
  },
  {
    id: "part-04-movable-face",
    name: "Polia Móvel Dianteira (Movable Drive Face)",
    partNumber: "22110-K0W-B00",
    oemSupplier: "Honda Alumínio Usinado",
    torqueSpec: "Deslizamento axial livre",
    serviceLimitMm: 24.06,
    nominalDimensionMm: 24.0,
    criticalGotcha:
      "Inspecionar face cônica contra ondulações causadas por uso severo. Verificar bucha interna de bronze sinterizado.",
    requiredTool: "Micrômetro interno / Súbito",
    baseZ: -35,
    explodedZ: 0,
    colorHex: "#e2e8f0",
    metalness: 0.85,
    roughness: 0.22,
  },
  {
    id: "part-05-boss-bushing",
    name: "Bucha Central Deslizante (Drive Face Boss)",
    partNumber: "22105-K97-T00",
    oemSupplier: "Aço Temperado Retificado",
    torqueSpec: "Assenta no batente do eixo",
    serviceLimitMm: 23.96,
    nominalDimensionMm: 23.98,
    criticalGotcha:
      "Diâmetro externo mínimo de 23,960 mm. Acabamento retificado cilíndrico de espelho para baixo atrito.",
    requiredTool: "Micrômetro externo 25-50 mm",
    baseZ: -10,
    explodedZ: 45,
    colorHex: "#f1f5f9",
    metalness: 0.98,
    roughness: 0.08,
  },
  {
    id: "part-06-drive-belt",
    name: "Correia de Transmissão V-Belt Bando Duplo Denteado",
    partNumber: "23100-K97-T01",
    oemSupplier: "Bando EPDM / Fibras de Kevlar",
    torqueSpec: "Protocolo Belt Sinking (Folga Obrigatória)",
    serviceLimitMm: 18.5,
    nominalDimensionMm: 19.5,
    criticalGotcha:
      "GOTCHA FATAL: A correia DEVE estar frouxa na frente durante o aperto. Se for mordida pelas polias, quebra o virabrequim!",
    requiredTool: "Paquímetro digital (limite de descarte: 18,5 mm)",
    baseZ: 0,
    explodedZ: 85,
    colorHex: "#18181b",
    metalness: 0.1,
    roughness: 0.9,
  },
  {
    id: "part-07-spacer-washer",
    name: "Arruela Espaçadora Plana de Encosto",
    partNumber: "90401-K97-T00",
    oemSupplier: "Aço estampado retificado",
    torqueSpec: "Assentamento plano",
    criticalGotcha:
      "Garante o espaçamento axial correto entre a bucha deslizante e as estrias da face fixa.",
    requiredTool: "Inspeção visual de paralelismo",
    baseZ: 10,
    explodedZ: 120,
    colorHex: "#64748b",
    metalness: 0.9,
    roughness: 0.25,
  },
  {
    id: "part-08-fixed-face",
    name: "Face Fixa Ventilada com Aletas Radiais (Drive Face)",
    partNumber: "22102-K97-T00",
    oemSupplier: "Honda Alumínio Fundido",
    torqueSpec: "Encaixe estriado direto no virabrequim",
    criticalGotcha:
      "PROIBIDO USO DE TALHADEIRAS! Travar exclusivamente pelos 2 furos reforçados com o Universal Holder Honda 07725-0030000.",
    requiredTool: "Universal Holder 07725-0030000",
    baseZ: 20,
    explodedZ: 165,
    colorHex: "#cbd5e1",
    metalness: 0.8,
    roughness: 0.28,
  },
  {
    id: "part-09-belleville-washer",
    name: "Arruela Cônica Elástica Belleville",
    partNumber: "90441-K97-T00",
    oemSupplier: "Aço mola cônico temperado",
    torqueSpec: "Sob pressão elástica da porca M14",
    criticalGotcha:
      "POSIÇÃO OBRIGATÓRIA: A inscrição gravada 'OUTSIDE' DEVE estar voltada para fora (sob a face da porca).",
    requiredTool: "Conferência visual de gravação",
    baseZ: 32,
    explodedZ: 215,
    colorHex: "#94a3b8",
    metalness: 0.92,
    roughness: 0.22,
  },
  {
    id: "part-10-nut-m14",
    name: "Porca Flangeada M14x1,25 (Sextavado 22 mm)",
    partNumber: "90201-GCC-000",
    oemSupplier: "Aço Classe 10.9 Zincado Bicromatizado",
    torqueSpec: "59 N.m (6,0 kgf.m)",
    criticalGotcha:
      "TORQUE CANÔNICO: 59 N.m com torquímetro calibrado. Proibido aperto com pistola de impacto pneumática descalibrada.",
    requiredTool: "Torquímetro de estalo + Soquete 22 mm (6 pontas)",
    baseZ: 42,
    explodedZ: 265,
    colorHex: "#eab308",
    metalness: 0.88,
    roughness: 0.2,
  },
];

export class Cvt3DExplosionEngine {
  private explosionProgress = 0.0; // 0.0 (montado) a 1.0 (totalmente explodido)
  private isXRayMode = false;
  private readonly parts: ReadonlyArray<Part3DConfig>;

  constructor(parts: ReadonlyArray<Part3DConfig> = CVT_PARTS_3D_METROLOGY) {
    if (!parts || parts.length === 0) {
      throw new Error("Cvt3DExplosionEngine: array de peças não pode ser vazio.");
    }
    this.parts = parts;
  }

  public getExplosionProgress(): number {
    return this.explosionProgress;
  }

  public setExplosionProgress(progress: number): void {
    this.explosionProgress = Math.max(0.0, Math.min(1.0, progress));
  }

  public isXRay(): boolean {
    return this.isXRayMode;
  }

  public toggleXRay(): boolean {
    this.isXRayMode = !this.isXRayMode;
    return this.isXRayMode;
  }

  public getPartConfig(partId: string): Part3DConfig | undefined {
    return this.parts.find((p) => p.id === partId);
  }

  public getAllParts(): ReadonlyArray<Part3DConfig> {
    return this.parts;
  }

  /**
   * Calcula a posição no eixo axial Z para cada peça baseado na interpolação linear (lerp)
   */
  public calculateAxialPositions(): Record<
    string,
    { z: number; opacity: number; wireframe: boolean }
  > {
    const result: Record<string, { z: number; opacity: number; wireframe: boolean }> = {};
    const t = this.explosionProgress;

    for (const part of this.parts) {
      // Interpolação linear da posição Z entre baseZ e explodedZ
      const interpolatedZ = Number((part.baseZ + (part.explodedZ - part.baseZ) * t).toFixed(2));

      // No modo raio-X, as carcaças e polias ficam semi-transparentes para ver o interior
      let opacity = 1.0;
      let wireframe = false;
      if (this.isXRayMode) {
        if (part.id === "part-04-movable-face" || part.id === "part-08-fixed-face") {
          opacity = 0.35;
          wireframe = true;
        } else {
          opacity = 0.95;
          wireframe = false;
        }
      }

      result[part.id] = {
        z: interpolatedZ,
        opacity,
        wireframe,
      };
    }

    return result;
  }
}
